import React from 'react';

import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import EmptyState from '@atlaskit/empty-state';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { Text } from '@atlaskit/primitives/compiled';
import FocusState from '@atlassian/eoc-focus-state';
import { IPHLink } from '@atlassian/eoc-iph-store';
import { useResource } from '@atlassian/eoc-rrr-helpers';
import { useMonitorRuleCards } from '@atlassian/eoc-rule-card';
import { Wrap } from '@atlassian/eoc-schedule-common';
import { useSpotlightActions } from '@atlassian/eoc-spotlight-store';

import { SPOTLIGHT_NAMES, SPOTLIGHT_TOURS } from '../../common/constants';
import { PageModalType, Scope } from '../../common/types';
import { Container as EditStoreContainer } from '../../controllers/edit-store';
import {
	Subscriber as PageStoreSubscriber,
	useActions as usePageActions,
} from '../../controllers/page-store';
import { useOrderUiPreference } from '../../controllers/preferences';
import { Container as ScheduleStoreContainer } from '../../controllers/schedule-store';
import type { ScheduleEntity } from '../../services/endpoints/list/types';
import { getScheduleRightsResource, getSchedulesResource } from '../../services/resources';
import { getSafe, isEmpty } from '../../utils/validators';

import { AddOverride } from './add-override';
import { DeleteRotation } from './delete-rotation';
import { DeleteSchedule } from './delete-schedule';
import { DiscardChanges } from './discard-changes';
import { EditSchedule } from './edit-schedule';
import { EmptyImage } from './empty-image';
import { messages } from './messages';
import { RevertOverride } from './revert-override';
import { Schedule } from './schedule';
import { ScheduleContainer } from './styled';
import { TakeOnCall } from './take-on-call';

const List = () => {
	const intlApi = useIntl();
	const { openCreateSchedule } = usePageActions();
	const { trigger } = useSpotlightActions();
	const { data: scheduleRights } = useResource(getScheduleRightsResource);
	const { data: schedulesResponse } = useResource(getSchedulesResource);
	const schedules = getSafe(() => schedulesResponse!.schedules, [] as ScheduleEntity[]);
	const { onDragEnd } = useOrderUiPreference();

	useMonitorRuleCards({
		droppableId: 'schedules',
		data: schedules,
		onComplete: ({ updatedValue }) => onDragEnd(updatedValue),
	});

	return (
		<>
			{isEmpty(schedules) && (
				<EmptyState
					renderImage={() => <EmptyImage />}
					imageHeight={160}
					imageWidth={160}
					header={intlApi.formatMessage(messages.yourTeamSchedulesAppear)}
					description={
						scheduleRights?.hasEditRight ? (
							<Text as="p">{intlApi.formatMessage(messages.startBuildingSchedules)} </Text>
						) : (
							<Text as="p">{intlApi.formatMessage(messages.seeOnCallOnceAdmin)} </Text>
						)
					}
					primaryAction={
						scheduleRights?.hasEditRight ? (
							<Button
								id="empty-state-create-schedule-button"
								appearance="primary"
								onClick={() =>
									openCreateSchedule({
										id: ['empty-state-create-schedule-button', 'create-schedule-button'],
									})
								}
							>
								{intlApi.formatMessage(messages.addSchedule)}
							</Button>
						) : undefined
					}
					tertiaryAction={
						<IPHLink id="on-call-schedules-empty-state" requiresDot>
							{intlApi.formatMessage(messages.readMoreAboutSchedules)}
						</IPHLink>
					}
				/>
			)}
			{!isEmpty(schedules) && (
				<ScheduleContainer>
					{schedules!.map((schedule, index) => (
						<ScheduleStoreContainer key={schedule.id} scope={schedule.id} schedule={schedule}>
							<Wrap
								within={(e) => (
									<SpotlightTarget name={SPOTLIGHT_NAMES.firstSchedule}>{e}</SpotlightTarget>
								)}
								when={index === 0}
							>
								<Schedule index={index} />
							</Wrap>
						</ScheduleStoreContainer>
					))}
				</ScheduleContainer>
			)}
			<PageStoreSubscriber>
				{(
					{
						// NOTE: might be able to set active schedule internally, schedule id might be sufficent
						activeSchedule,
						activeOverride,
						activeRotation,
						discardVariant,
						overrideVariant,
						isEditModeOpen,
						isTakeOnCallOpen,
						isAddOverrideOpen,
						isDeleteScheduleOpen,
						isRevertOverrideOpen,
						isDeleteRotationOpen,
						isDiscardChangesOpen,
					},
					{ closePageModal, focusSource },
				) => (
					<>
						<FocusState
							// TODO: add close on escape
							isOpen={isEditModeOpen}
							onAnimationFinish={(transition) => {
								if (transition === 'entering') {
									trigger(SPOTLIGHT_TOURS.rotationList);
								}

								if (transition === 'exiting') {
									focusSource();
								}
							}}
						>
							<ScheduleStoreContainer
								// WARNING: Shouldn't have to use "find" here
								scope={Scope.Edit}
								schedule={schedules.find(({ id }) => id === activeSchedule?.id)}
							>
								<EditStoreContainer scope={Scope.Edit}>
									<EditSchedule />
								</EditStoreContainer>
							</ScheduleStoreContainer>
						</FocusState>
						<ModalTransition>
							{isTakeOnCallOpen && activeSchedule && (
								<Modal
									autoFocus
									onCloseComplete={focusSource}
									onClose={() => closePageModal(PageModalType.TakeOnCall)}
									width="small"
								>
									<ScheduleStoreContainer scope={activeSchedule.id}>
										<TakeOnCall onClose={() => closePageModal(PageModalType.TakeOnCall)} />
									</ScheduleStoreContainer>
								</Modal>
							)}
						</ModalTransition>
						<ModalTransition>
							{isRevertOverrideOpen && activeSchedule && (
								<Modal
									autoFocus
									onCloseComplete={focusSource}
									onClose={() => closePageModal(PageModalType.RevertOverride)}
									width="small"
								>
									<ScheduleStoreContainer scope={activeSchedule.id}>
										<RevertOverride
											alias={activeOverride!.alias!}
											onClose={() => closePageModal(PageModalType.RevertOverride)}
										/>
									</ScheduleStoreContainer>
								</Modal>
							)}
						</ModalTransition>
						<ModalTransition>
							{isAddOverrideOpen && overrideVariant && activeSchedule && (
								<Modal
									autoFocus
									onCloseComplete={focusSource}
									onClose={() => closePageModal(PageModalType.AddOverride)}
									width="small"
								>
									<ScheduleStoreContainer scope={activeSchedule.id}>
										<AddOverride
											onClose={() => closePageModal(PageModalType.AddOverride)}
											baseOverride={activeOverride}
											variant={overrideVariant}
										/>
									</ScheduleStoreContainer>
								</Modal>
							)}
						</ModalTransition>
						<ModalTransition>
							{isDeleteScheduleOpen && activeSchedule && (
								<Modal
									autoFocus
									onCloseComplete={focusSource}
									onClose={() => closePageModal(PageModalType.DeleteSchedule)}
									width="small"
								>
									<ScheduleStoreContainer scope={activeSchedule.id}>
										<DeleteSchedule onClose={() => closePageModal(PageModalType.DeleteSchedule)} />
									</ScheduleStoreContainer>
								</Modal>
							)}
						</ModalTransition>
						<ModalTransition>
							{isEditModeOpen && isDeleteRotationOpen && activeSchedule && activeRotation && (
								<Modal
									autoFocus
									onCloseComplete={focusSource}
									onClose={() => closePageModal(PageModalType.DeleteRotation)}
									width="small"
								>
									<ScheduleStoreContainer scope={Scope.Edit}>
										<EditStoreContainer scope={Scope.Edit}>
											<DeleteRotation
												baseRotation={activeRotation}
												onClose={() => closePageModal(PageModalType.DeleteRotation)}
											/>
										</EditStoreContainer>
									</ScheduleStoreContainer>
								</Modal>
							)}
						</ModalTransition>
						<ModalTransition>
							{isEditModeOpen && isDiscardChangesOpen && activeSchedule && discardVariant && (
								<Modal
									autoFocus
									onCloseComplete={focusSource}
									onClose={() => closePageModal(PageModalType.DiscardChanges)}
									width="small"
								>
									<ScheduleStoreContainer scope={Scope.Edit}>
										<EditStoreContainer scope={Scope.Edit}>
											<DiscardChanges
												variant={discardVariant}
												onClose={() => closePageModal(PageModalType.DiscardChanges)}
											/>
										</EditStoreContainer>
									</ScheduleStoreContainer>
								</Modal>
							)}
						</ModalTransition>
					</>
				)}
			</PageStoreSubscriber>
		</>
	);
};

export { List };
